import { makeAutoObservable } from 'mobx'
import { MAX_CRM_LENGTH } from 'shared/constants/auth'
import { type IResponseUsersProfilesUiSettings } from 'entities/Users/api/types'
import { type IParamsRegisterAnswersStep2 } from 'entities/Auth'
import {
  type ICrm,
  CrmEnum,
  FieldsEnum,
  ContactsCountsEnum,
  type IParamsRegisterGtmAnswersStep2,
} from './type'

export class Step2Store {
  private _crm: ICrm | null = null
  private _contactsCount = ''
  private _activeField: FieldsEnum | null = null

  constructor() {
    makeAutoObservable(this)
  }

  get payload() {
    return {
      answer_integration: this.crm?.name || '',
      answer_contactsCount: this._contactsCount,
    }
  }

  get answers(): IParamsRegisterAnswersStep2 {
    return {
      crm: this.crm?.value || '',
      contacts_number: this._contactsCount,
    }
  }

  get gtmAnswers(): IParamsRegisterGtmAnswersStep2 {
    return {
      crm: this.crm?.value || '',
      contacts_number: this._contactsCount,
    }
  }

  get isCompletedStep() {
    return !!this.crm && !!this.contactsCount
  }

  get isActiveCrmField() {
    return this._activeField === FieldsEnum.crm
  }

  reset = () => {
    this._crm = null
    this._contactsCount = ''
    this._activeField = null
  }

  initAnswers = ({ data }: IResponseUsersProfilesUiSettings) => {
    if (!data) return

    const { answer_integration, answer_contactsCount } = data.items.answers

    if (answer_integration) {
      const crm = this.crms.find((item) => item.name === answer_integration)
      if (crm) this._crm = crm

      if (crm) {
        this._crm = crm
      } else {
        this._crm = {
          id: CrmEnum.other,
          value: 'Other',
          name: answer_integration,
          icon: 'lightning2',
        }
      }
    }

    if (answer_contactsCount) this._contactsCount = answer_contactsCount
  }

  get crm() {
    return this._crm
  }

  get crmLength() {
    if (!this._crm) return 0
    return this._crm.name.length
  }

  get contactsCount() {
    return this._contactsCount
  }

  get crms(): ICrm[] {
    return [
      {
        id: CrmEnum.hubSpot,
        value: 'HubSpot',
        name: 'HubSpot',
        icon: 'hubspot',
      },
      {
        id: CrmEnum.activeCampaign,
        value: 'ActiveCampaign',
        name: 'ActiveCampaign',
        icon: 'activecampaign',
      },
      {
        id: CrmEnum.keap,
        value: 'Keap (Infusionsoft)',
        name: 'Keap',
        icon: 'infusionsoft',
      },
      {
        id: CrmEnum.salesforce,
        value: 'Salesforce',
        name: 'Salesforce',
        icon: 'salesforce',
      },
      {
        id: CrmEnum.pipedrive,
        value: 'Pipedrive',
        name: 'Pipedrive',
        icon: 'pipedrive',
      },
      {
        id: CrmEnum.none,
        value: 'None',
        name: 'None',
        icon: 'block',
      },
      {
        id: CrmEnum.other,
        value: 'Other',
        name: 'Other',
        icon: 'lightning2',
      },
    ]
  }

  get contactsCounts(): string[] {
    return [
      ContactsCountsEnum.count_0_1K,
      ContactsCountsEnum.count_1K_10K,
      ContactsCountsEnum.count_10K_50K,
      ContactsCountsEnum.count_50K_100K,
      ContactsCountsEnum.count_100K_250K,
      ContactsCountsEnum.count_250K_andMore,
    ]
  }

  get isNextDisabled() {
    return Boolean(!this._crm || this._crm.name.length > MAX_CRM_LENGTH || !this._contactsCount)
  }

  setContactsCount = (value: string) => {
    this._contactsCount = value
  }

  setCrm = (crm: ICrm | null) => {
    if (crm && crm.name.length > MAX_CRM_LENGTH) return
    this._crm = crm
  }

  handleBlur = () => {
    this._activeField = null
  }

  setActiveField = (value: FieldsEnum | null) => {
    this._activeField = value
  }

  handleCrmFieldFocus = () => {
    this.setActiveField(FieldsEnum.crm)
  }
}
